import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Divider,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Alert,
    CircularProgress,
    TextField,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import {
    getFirestore,
    collection,
    query,
    where,
    getDocs,
    Timestamp,
    doc,
    getDoc,
} from 'firebase/firestore';
import { toast } from 'react-toastify';
import Fiananceheader from './Fiananceheader';

const db = getFirestore();

function Emireport() {
    const today = new Date().toISOString().split("T")[0];
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [emiData, setEmiData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [reportGenerated, setReportGenerated] = useState(false);
    const [error, setError] = useState('');

    const printRef = useRef();

    const fetchEmiData = async () => {
        setError('');
        if (!fromDate || !toDate) {
            setError('Please select both From Date and To Date.');
            return;
        }

        const fromTimestamp = Timestamp.fromDate(new Date(fromDate));
        const toTimestamp = Timestamp.fromDate(new Date(`${toDate}T23:59:59`));

        setLoading(true);
        try {
            const emiQuery = query(
                collection(db, 'loanemis'),
                where('paydate', '>=', fromTimestamp),
                where('paydate', '<=', toTimestamp)
            );
            const emiSnapshot = await getDocs(emiQuery);
            const emiList = emiSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            if (emiList.length === 0) {
                setEmiData([]);
                setReportGenerated(true);
                setLoading(false);
                return;
            }

            const loanIds = [...new Set(emiList.map(emi => emi.loanid))];
            const loanPromises = loanIds.map(id => getDoc(doc(db, 'loans', id)));
            const loanSnapshots = await Promise.all(loanPromises);

            const loanMap = loanSnapshots.reduce((acc, loanDoc) => {
                if (loanDoc.exists()) {
                    acc[loanDoc.id] = loanDoc.data().custid;
                }
                return acc;
            }, {});

            const custIds = [...new Set(Object.values(loanMap))];
            const customerPromises = custIds.map(id => getDoc(doc(db, 'fianancecustomers', id)));
            const customerSnapshots = await Promise.all(customerPromises);

            const customerMap = customerSnapshots.reduce((acc, customerDoc) => {
                if (customerDoc.exists()) {
                    acc[customerDoc.id] = customerDoc.data().name;
                }
                return acc;
            }, {});

            const combinedData = emiList.map(emi => {
                const custid = loanMap[emi.loanid];
                return {
                    ...emi,
                    customerName: customerMap[custid] || 'N/A',
                };
            });

            setEmiData(combinedData);
            setReportGenerated(true);
        } catch (err) {
            console.error('Error fetching EMI report:', err);
            toast.error('Failed to generate report.');
            setError('Failed to fetch data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmiData();
    }, []);

    const handlePrint = () => {
        const content = printRef.current.innerHTML;
        const printWindow = window.open("", "_blank");
        printWindow.document.write(`
          <html>
            <head>
              <title>EMI Report</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                th, td { border: 1px solid #000; padding: 8px; text-align: left; }
                th { background-color: #1976d2; color: #fff; }
                h2,h4 { margin: 5px 0; }
              </style>
            </head>
            <body>
              ${content}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    const totalAmount = emiData.reduce((sum, emi) => sum + Number(emi.amount || 0), 0);

    return (
        <Box p={3}>
            <Fiananceheader />
            <Paper sx={{ p: 4, borderRadius: '5px' }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', color: '#1976d2', mb: 3 }}>
                    📊 हप्ता रिपोर्ट
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2} mb={3}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="या तारखेपासून"
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="या तारखेपर्यंत"
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={fetchEmiData}
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} /> : 'Get Report'}
                        </Button>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {reportGenerated && emiData.length === 0 && !loading && (
                    <Alert severity="info" sx={{ mb: 3 }}>No EMI payments found for the selected date range.</Alert>
                )}

                {emiData.length > 0 && (
                    <Box sx={{ mt: 3, textAlign: 'right' }}>
                        <Button variant="outlined" onClick={handlePrint} startIcon={<PrintIcon />}>
                            Print Report
                        </Button>
                    </Box>
                )}

                <div ref={printRef} style={{ padding: '16px' }}>
                    {emiData.length > 0 && (
                        <>
                            <Typography variant="h6" align="center" sx={{ display: 'none', '@media print': { display: 'block' } }}>
                                या तारखेपासून {new Date(fromDate).toLocaleDateString()} या तारखेपर्यंत {new Date(toDate).toLocaleDateString()}
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Sr. No.</TableCell>
                                            <TableCell>कर्जदाराचे नाव</TableCell>
                                            <TableCell>रक्कम</TableCell>
                                            <TableCell>जमा तारीख</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {emiData
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((emi, index) => (
                                                <TableRow key={emi.id}>
                                                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                                    <TableCell>{emi.customerName}</TableCell>
                                                    <TableCell>₹{emi.amount}</TableCell>
                                                    <TableCell>{emi.paydate && new Date(emi.paydate.seconds * 1000).toLocaleDateString()}</TableCell>
                                                </TableRow>
                                            ))}
                                        {/* Total Amount Row */}
                                        <TableRow>
                                            <TableCell colSpan={2} sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total Amount:</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>₹{totalAmount}</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={emiData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, newPage) => setPage(newPage)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </>
                    )}
                </div>
            </Paper>
        </Box>
    );
}

export default Emireport;
